import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { useMetaTags } from '../../hooks/useMetaTags';
import { api } from '../../utils/apiClient';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';

function Account() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isLoggedIn, updateEmail } = useUser();
  
  // Email change state
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [emailChangeError, setEmailChangeError] = useState('');
  const [emailChangeSuccess, setEmailChangeSuccess] = useState('');
  const [isEmailChangeSubmitting, setIsEmailChangeSubmitting] = useState(false);
  
  // Password change state
  const [currentPasswordForChange, setCurrentPasswordForChange] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState('');
  const [isPasswordChangeSubmitting, setIsPasswordChangeSubmitting] = useState(false);
  
  // Set meta tags
  useMetaTags(
    t('account.title', 'Account Settings'),
    t('account.description', 'Manage your account settings')
  );
  
  // Redirect if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login?redirect=/account');
    }
  }, [isLoggedIn, navigate]);
  
  // Pre-fill email field with current email
  useEffect(() => {
    if (user.email) {
      setNewEmail(user.email);
    }
  }, [user.email]);
  
  // Validate password
  const validatePassword = (pass: string): boolean => {
    // Minimum length check
    if (pass.length < 8) {
      setPasswordError(t('auth.passwordTooShort', 'Password must be at least 8 characters long'));
      return false;
    }
    
    // Reset error
    setPasswordError('');
    return true;
  };
  
  // Check if passwords match
  const validatePasswordMatch = (pass1: string, pass2: string): boolean => {
    if (pass1 !== pass2) {
      setConfirmPasswordError(t('auth.passwordsDontMatch', 'Passwords do not match'));
      return false;
    }
    
    // Reset error
    setConfirmPasswordError('');
    return true;
  };
  
  // Handle email change form submission
  const handleEmailChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset messages
    setEmailChangeError('');
    setEmailChangeSuccess('');
    
    // Basic validation
    if (!newEmail || !newEmail.includes('@')) {
      setEmailChangeError(t('auth.invalidEmail', 'Please enter a valid email address'));
      return;
    }
    
    if (!currentPassword) {
      setEmailChangeError(t('auth.passwordRequired', 'Current password is required to change your email'));
      return;
    }
    
    // Skip API call if email hasn't changed
    if (newEmail === user.email) {
      setEmailChangeError(t('account.emailUnchanged', 'The new email is the same as your current email'));
      return;
    }
    
    try {
      setIsEmailChangeSubmitting(true);
      
      // Verify password first
      const passwordVerified = await api.auth.verifyPassword(currentPassword);
      
      if (!passwordVerified.success) {
        setEmailChangeError(t('auth.incorrectPassword', 'Current password is incorrect'));
        return;
      }
      
      // Call API to update email
      const success = await updateEmail(newEmail, currentPassword);
      
      if (success) {
        setEmailChangeSuccess(t(
          'account.emailChangeRequested', 
          'Email change requested. Please check your new email address for a confirmation link.'
        ));
        setCurrentPassword('');
      } else {
        setEmailChangeError(t('account.emailChangeFailed', 'Failed to update email. Please try again.'));
      }
    } catch (error) {
      console.error('Email change error:', error);
      setEmailChangeError(t('account.emailChangeFailed', 'Failed to update email. Please try again.'));
    } finally {
      setIsEmailChangeSubmitting(false);
    }
  };
  
  // Handle password change form submission
  const handlePasswordChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset messages
    setPasswordChangeError('');
    setPasswordChangeSuccess('');
    setPasswordError('');
    setConfirmPasswordError('');
    
    // Validate all fields
    if (!currentPasswordForChange) {
      setPasswordChangeError(t('auth.currentPasswordRequired', 'Current password is required'));
      return;
    }
    
    const isPasswordValid = validatePassword(newPassword);
    const doPasswordsMatch = validatePasswordMatch(newPassword, confirmNewPassword);
    
    if (!isPasswordValid || !doPasswordsMatch) {
      return;
    }
    
    try {
      setIsPasswordChangeSubmitting(true);
      
      // Verify current password
      const passwordVerified = await api.auth.verifyPassword(currentPasswordForChange);
      
      if (!passwordVerified.success) {
        setPasswordChangeError(t('auth.incorrectPassword', 'Current password is incorrect'));
        return;
      }
      
      // Change password
      const response = await api.auth.changePassword(currentPasswordForChange, newPassword);
      
      if (response.success) {
        setPasswordChangeSuccess(t('account.passwordChangeSuccess', 'Your password has been changed successfully'));
        setCurrentPasswordForChange('');
        setNewPassword('');
        setConfirmNewPassword('');
      } else {
        setPasswordChangeError(response.message || t('account.passwordChangeFailed', 'Failed to change password. Please try again.'));
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordChangeError(t('account.passwordChangeFailed', 'Failed to change password. Please try again.'));
    } finally {
      setIsPasswordChangeSubmitting(false);
    }
  };
  
  if (!isLoggedIn) {
    return null; // Don't render anything if not logged in (we'll redirect)
  }
  
  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-12">
          <h1 className="h3 mb-4">{t('account.title', 'Account Settings')}</h1>
          
          <div className="row g-4">
            {/* Email Change Section */}
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm h-100">
                <div className="card-header">
                  <h2 className="h5 mb-0">{t('account.changeEmail', 'Change Email Address')}</h2>
                </div>
                <div className="card-body">
                  {emailChangeSuccess ? (
                    <div className="alert alert-success" role="alert">
                      {emailChangeSuccess}
                    </div>
                  ) : (
                    <>
                      <p className="text-muted mb-3">
                        {t('account.emailChangeDesc', 'Update your email address. You\'ll need to verify the new address.')}
                      </p>
                      
                      {emailChangeError && (
                        <div className="alert alert-danger" role="alert">
                          {emailChangeError}
                        </div>
                      )}
                      
                      <form onSubmit={handleEmailChangeSubmit}>
                        <div className="mb-3">
                          <label htmlFor="newEmail" className="form-label">
                            {t('account.newEmail', 'New Email Address')}
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            id="newEmail"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="you@example.com"
                            required
                          />
                        </div>
                        
                        <div className="mb-3">
                          <label htmlFor="currentPassword" className="form-label">
                            {t('account.currentPassword', 'Current Password')}
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            id="currentPassword"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder={t('account.enterCurrentPassword', 'Enter your current password')}
                            required
                            aria-describedby="currentPasswordHelp"
                          />
                          <div id="currentPasswordHelp" className="form-text">
                            {t('account.passwordRequiredForSecurity', 'Required for security verification')}
                          </div>
                        </div>
                        
                        <div className="d-grid">
                          <button 
                            type="submit" 
                            className="btn btn-primary" 
                            disabled={isEmailChangeSubmitting}
                          >
                            {isEmailChangeSubmitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {t('common.loading', 'Loading...')}
                              </>
                            ) : (
                              t('account.updateEmail', 'Update Email')
                            )}
                          </button>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Password Change Section */}
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm h-100">
                <div className="card-header">
                  <h2 className="h5 mb-0">{t('account.changePassword', 'Change Password')}</h2>
                </div>
                <div className="card-body">
                  {passwordChangeSuccess ? (
                    <div className="alert alert-success" role="alert">
                      {passwordChangeSuccess}
                      <button 
                        className="btn btn-sm btn-outline-success mt-2"
                        onClick={() => setPasswordChangeSuccess('')}
                      >
                        {t('account.acknowledged', 'Acknowledged')}
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-muted mb-3">
                        {t('account.passwordChangeDesc', 'Update your password. Choose a strong, unique password.')}
                      </p>
                      
                      {passwordChangeError && (
                        <div className="alert alert-danger" role="alert">
                          {passwordChangeError}
                        </div>
                      )}
                      
                      <form onSubmit={handlePasswordChangeSubmit}>
                        <div className="mb-3">
                          <label htmlFor="currentPasswordForChange" className="form-label">
                            {t('account.currentPassword', 'Current Password')}
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            id="currentPasswordForChange"
                            value={currentPasswordForChange}
                            onChange={(e) => setCurrentPasswordForChange(e.target.value)}
                            placeholder={t('account.enterCurrentPassword', 'Enter your current password')}
                            required
                            aria-describedby="currentPasswordForChangeHelp"
                          />
                          <div id="currentPasswordForChangeHelp" className="form-text">
                            {t('account.verifyCurrentPassword', 'Enter your current password to verify your identity')}
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <label htmlFor="newPassword" className="form-label">
                            {t('account.newPassword', 'New Password')}
                          </label>
                          <input
                            type="password"
                            className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder={t('account.enterNewPassword', 'Enter new password (min. 8 characters)')}
                            required
                            minLength={8}
                            aria-describedby="newPasswordHelp"
                          />
                          <div id="newPasswordHelp" className="form-text">
                            {t('account.passwordRequirements', 'Use at least 8 characters with a mix of letters, numbers and symbols')}
                          </div>
                          {passwordError && (
                            <div className="invalid-feedback">{passwordError}</div>
                          )}
                          {newPassword && <PasswordStrengthMeter password={newPassword} />}
                        </div>
                        
                        <div className="mb-4">
                          <label htmlFor="confirmNewPassword" className="form-label">
                            {t('account.confirmNewPassword', 'Confirm New Password')}
                          </label>
                          <input
                            type="password"
                            className={`form-control ${confirmPasswordError ? 'is-invalid' : ''}`}
                            id="confirmNewPassword"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            placeholder={t('account.confirmNewPasswordPlaceholder', 'Re-enter your new password')}
                            required
                            minLength={8}
                          />
                          {confirmPasswordError && (
                            <div className="invalid-feedback">{confirmPasswordError}</div>
                          )}
                        </div>
                        
                        <div className="d-grid">
                          <button 
                            type="submit" 
                            className="btn btn-primary" 
                            disabled={isPasswordChangeSubmitting}
                          >
                            {isPasswordChangeSubmitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {t('common.loading', 'Loading...')}
                              </>
                            ) : (
                              t('account.updatePassword', 'Update Password')
                            )}
                          </button>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Account; 